import { globalHistory } from "@reach/router";
import { useLocation } from '@reach/router';
import { isPlpResetAfterLoginPending } from '../../utils/sessionStorageUtils';

export interface HLocation<S = unknown> {
  pathname: string;
  search: string;
  state: S;
  hash: string;
  key?: string | undefined;
}

export enum RouterActionEnum {
  PUSH = "KP_PUSH",
}

export type HistoryListener = (parameter: HistoryListenerParameter) => void;

export type HistoryUnsubscribe = () => void;

export type WindowLocation<S = unknown> = Window["location"] & HLocation<S>;
export type HistoryLocation = WindowLocation & {
  state?: any;
  query: Record<string, string>;
};
export interface HistoryListenerParameter {
  location: HistoryLocation;
  action: any;
}
export interface NavigateOptions<TState> {
  state?: TState | undefined;
  replace?: boolean | undefined;
}

export interface NavigateFn {
  (to: string, options?: NavigateOptions<{}>): Promise<void>;
}

interface KPHistory {
  location: HistoryLocation;
  broadcast: (param: HistoryListenerParameter) => void;
  listen: (listener: HistoryListener) => HistoryUnsubscribe;
  navigate: NavigateFn;
}

/**
 * Get the current URL parameters.
 * 
 * @returns An object containing the URL parameters.
 */
const useUrlParams = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const params: { [key: string]: string | string[] } = {};

  searchParams.forEach((value, key) => {
    if (value.includes(',')) {
      params[key] = value.split(',');
    } else {
      params[key] = value;
    }
  });

  return params;
};

const unwrapPLPQueryParams = () => {

  if (isPlpResetAfterLoginPending()) {
    const rawSearch = globalHistory.location?.search || '';
    const rawStr = rawSearch.startsWith('?') ? rawSearch.substring(1) : rawSearch;
    const typeSegment = rawStr.split('|').find((s: string) => s.startsWith('type='));
    if (typeSegment) {
      const [, val] = typeSegment.split('=');
      return { type: val?.includes(',') ? val.split(',') : val } as Record<string, string | string[]>;
    }
    return {} as Record<string, string | string[]>;
  }

  const history = globalHistory;
  let querystr = history.location?.search || "";
  
  if (querystr.startsWith("?")) {
    querystr = querystr.substring(1);
  }
  const searchParamsTest = Object.fromEntries(
    querystr.split("|").map(pair => {
      const [key, value] = pair.split("=");
      return [key, value?.includes(",") ? value?.split(",") : value];
    })
  );
  
  return searchParamsTest;
};

const createHistory = (): KPHistory => {
  const history = globalHistory;
  let listeners: HistoryListener[] = [];

  const urlParams = new URLSearchParams(history.location.search);
  const location: HistoryLocation = {
    ...history.location,
    query: Object.fromEntries(urlParams),
  };
  return {
    location,
    broadcast(param: HistoryListenerParameter) {
      listeners.forEach((listener) => listener(param));
    },

    listen(listener: HistoryListener): HistoryUnsubscribe {
      listeners.push(listener);
      history.listen(listener as any);
      return () => {
        listeners = listeners.filter((fn) => fn !== listener);
      };
    },

    async navigate(to: string, options: NavigateOptions<any> = {}) {
      listeners.forEach((listener) =>
        listener({ location, action: RouterActionEnum.PUSH })
      );
      if (window?.location) {
        window.location?.assign(to)
      }
      return;
    },
  };
};


const kpHistory = createHistory();
const { navigate, listen } = kpHistory;

export { kpHistory, listen, navigate, useUrlParams, unwrapPLPQueryParams };
